import { cancelSpeech, isSpeechSupported } from "./speech";

export type ArticleSpeechState = "idle" | "playing" | "paused";

interface ActiveSession {
  id: string;
  notify: (state: ArticleSpeechState) => void;
}

let active: ActiveSession | null = null;

// Browsers get flaky with a single very long utterance, so long articles are
// split into smaller chunks and queued; speechSynthesis plays queued
// utterances back-to-back on its own.
function chunkForSpeech(text: string, maxLength = 1600): string[] {
  const sentences = text.split(/(?<=[.!?])\s+/).filter(Boolean);
  const chunks: string[] = [];
  let current = "";

  for (const sentence of sentences) {
    const candidate = current ? `${current} ${sentence}` : sentence;
    if (candidate.length > maxLength && current) {
      chunks.push(current);
      current = sentence;
    } else {
      current = candidate;
    }
  }
  if (current) chunks.push(current);
  return chunks.length > 0 ? chunks : [text];
}

function clearActive(id: string) {
  if (active?.id === id) {
    active = null;
  }
}

// Stops whichever article session is currently playing/paused, if any. Safe
// to call with no argument to stop unconditionally, or with an id to only
// stop if that id owns the active session.
export function stopArticleSpeech(id?: string): void {
  if (id && active?.id !== id) return;
  if (isSpeechSupported()) {
    window.speechSynthesis.cancel();
  }
  const previous = active;
  active = null;
  previous?.notify("idle");
}

// Splits text into chunks for speech and returns the exact string those
// chunks reconstruct to (chunks.join(" ")) alongside each chunk's starting
// offset within that reconstruction — callers that render text for
// highlighting must render THIS string so char offsets from boundary events
// line up with what's on screen.
export function prepareSpeechText(text: string): { normalizedText: string; chunks: string[]; chunkOffsets: number[] } {
  const chunks = chunkForSpeech(text.trim());
  const chunkOffsets: number[] = [];
  let offset = 0;
  for (const chunk of chunks) {
    chunkOffsets.push(offset);
    offset += chunk.length + 1; // +1 for the space joining this chunk to the next
  }
  return { normalizedText: chunks.join(" "), chunks, chunkOffsets };
}

export function startArticleSpeech(
  id: string,
  text: string,
  notify: (state: ArticleSpeechState) => void,
  onBoundary?: (charIndex: number, charLength: number) => void
): string {
  // Enforce a single audio session across the whole app: stop any other
  // article session and any quote/book "read aloud" session in flight.
  stopArticleSpeech();
  cancelSpeech();

  const { normalizedText, chunks, chunkOffsets } = prepareSpeechText(text);
  const utterances = chunks.map((chunk) => new SpeechSynthesisUtterance(chunk));

  utterances.forEach((utterance, index) => {
    utterance.rate = 1;
    utterance.pitch = 1;
    utterance.volume = 1;
    const chunkOffset = chunkOffsets[index];
    utterance.onboundary = (event: SpeechSynthesisEvent) => {
      if (active?.id !== id) return;
      onBoundary?.(chunkOffset + event.charIndex, event.charLength || 0);
    };
    utterance.onerror = () => {
      if (active?.id !== id) return;
      clearActive(id);
      notify("idle");
    };
    if (index === utterances.length - 1) {
      utterance.onend = () => {
        if (active?.id !== id) return;
        clearActive(id);
        notify("idle");
      };
    }
  });

  active = { id, notify };
  utterances.forEach((utterance) => window.speechSynthesis.speak(utterance));
  notify("playing");

  return normalizedText;
}

export function pauseArticleSpeech(id: string): void {
  if (active?.id !== id || !isSpeechSupported()) return;
  window.speechSynthesis.pause();
  active.notify("paused");
}

export function resumeArticleSpeech(id: string): void {
  if (active?.id !== id || !isSpeechSupported()) return;
  window.speechSynthesis.resume();
  active.notify("playing");
}
